import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Header.css";
import { FaBars } from "react-icons/fa";
import {
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { MdLogout, MdSettings, MdPerson } from "react-icons/md";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { AuthContext } from "../../context/AuthContext";
import useAxios from "../../hooks/useAxios";
import Logo from "../../assets/images/logo.png";

// Import the separate DrawerMenu component
import DrawerMenu from "./DrawerMenu";

const Header = ({ navLinks, isScrollEffect }) => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [profileExpanded, setProfileExpanded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxios();

  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);

  const { token, role, status } = useContext(AuthContext);

  // Check if user is in an active quiz session
  const isInQuizSession = location.pathname.match(/^\/sessions\/\d+$/);

  // Fetch User Data
  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/user");
      if (response.data.success) {
        setUserData(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Scroll effect
  useEffect(() => {
    if (!isScrollEffect) {
      setScrolled(true);
      return;
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrollEffect]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token]);

  // Handle Avatar Menu (on larger screens)
  const handleAvatarClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Logout
  const handleLogout = () => {
    // your logout logic
    navigate("/logout");
  };

  // Status styling
  const getStatusStyle = () => {
    switch (status) {
      case "active":
        return { color: "green", text: "Active" };
      case "pending":
        return { color: "orange", text: "Pending" };
      case "trial":
        return { color: "orange", text: "Trial" };
      case "suspended":
        return { color: "red", text: "Suspended" };
      case "blocked":
        return { color: "red", text: "Blocked" };
      default:
        return { color: "gray", text: "Unknown" };
    }
  };

  const skipTrial = () => {
    if (role === "school") {
      navigate("/setup-profile");
    } else {
      navigate("/purchase-plan");
    }
  };

  return (
    <>
      {/* Quiz Session Banner */}
      {/* {isInQuizSession && (
        <div className="quiz-session-banner">
          <Typography variant="body2" className="quiz-session-text">
            ⚠️ You are currently taking a quiz. Navigation is disabled to
            prevent accidental exits, use the end session button in the bottom
            right to finish session andexit.
          </Typography>
        </div>
      )} */}

      <header
        className={`header ${
          scrolled ? "header-scrolled" : "header-transparent"
        } ${isInQuizSession ? "with-banner" : ""}`}
      >
        <div className="header-container">
          {/* Logo */}
          {/* <h1 className="logo">EDUWaves</h1> */}
          <img src={Logo} alt="EDUWaves logo" className="logo" />

          {/* Navigation Links (visible on large screens) */}
          <nav className="nav-links">
            {token ? (
              <>
                {/* HOME BUTTON (conditionally shown) */}
                {location.pathname !== "/dashboard" &&
                  location.pathname !== "/free-trial" &&
                  (status === "trial" || status === "pending" ? (
                    <>
                      <Link
                        to="/free-trial"
                        className={`nav-link ${
                          isInQuizSession ? "disabled" : ""
                        }`}
                        onClick={
                          isInQuizSession
                            ? (e) => e.preventDefault()
                            : undefined
                        }
                        style={
                          isInQuizSession
                            ? { pointerEvents: "none", opacity: 0.5 }
                            : {}
                        }
                      >
                        Home
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/dashboard"
                        className={`nav-link ${
                          isInQuizSession ? "disabled" : ""
                        }`}
                        onClick={
                          isInQuizSession
                            ? (e) => e.preventDefault()
                            : undefined
                        }
                        style={
                          isInQuizSession
                            ? { pointerEvents: "none", opacity: 0.5 }
                            : {}
                        }
                      >
                        Home
                      </Link>
                    </>
                  ))}
                {location.pathname !== "/owner-quizzes" &&
                  (status === "trial" ? (
                    <>
                      <Link
                        to="/owner-quizzes"
                        className={`nav-link ${
                          isInQuizSession ? "disabled" : ""
                        }`}
                        onClick={
                          isInQuizSession
                            ? (e) => e.preventDefault()
                            : undefined
                        }
                        style={
                          isInQuizSession
                            ? { pointerEvents: "none", opacity: 0.5 }
                            : {}
                        }
                      >
                        Dashboard
                      </Link>
                    </>
                  ) : (
                    <></>
                  ))}

                {/* Dynamically render nav links */}
                {navLinks?.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link ${isInQuizSession ? "disabled" : ""}`}
                    onClick={
                      isInQuizSession ? (e) => e.preventDefault() : undefined
                    }
                    style={
                      isInQuizSession
                        ? { pointerEvents: "none", opacity: 0.5 }
                        : {}
                    }
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Students Navigation Link for relevant roles */}
                {(role === "family" ||
                  role === "teacher" ||
                  role === "school") && (
                  <Link
                    to="/students"
                    className={`nav-link ${isInQuizSession ? "disabled" : ""}`}
                    onClick={
                      isInQuizSession ? (e) => e.preventDefault() : undefined
                    }
                    style={
                      isInQuizSession
                        ? { pointerEvents: "none", opacity: 0.5 }
                        : {}
                    }
                  >
                    Students
                  </Link>
                )}

                {/* Avatar for user dropdown */}
                <Badge color={getStatusStyle().color}>
                  <Avatar
                    onClick={isInQuizSession ? undefined : handleAvatarClick}
                    className={`avatar-icon ${
                      isInQuizSession ? "disabled" : ""
                    }`}
                    style={
                      isInQuizSession
                        ? {
                            pointerEvents: "none",
                            opacity: 0.5,
                            cursor: "not-allowed",
                          }
                        : {}
                    }
                  >
                    {userData?.name ? (
                      userData.name[0].toUpperCase()
                    ) : (
                      <MdPerson />
                    )}
                  </Avatar>
                </Badge>
                {/* Menu */}
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  disableScrollLock
                  disablePortal
                  keepMounted
                  PaperProps={{
                    style: {
                      backgroundColor: "var(--light-gray)",
                      padding: "16px",
                      borderRadius: "12px",
                      boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                  MenuListProps={{
                    style: {
                      padding: 0,
                    },
                  }}
                >
                  <Box className="profile-card">
                    {/* <Avatar className="avatar-icon">
                      {userData?.name ? userData.name[0].toUpperCase() : <MdPerson />}
                    </Avatar> */}
                    <Typography
                      variant="h6"
                      className="pt-4 capitalize-text profile-card-text"
                    >
                      {userData?.name || "Guest User"}
                    </Typography>
                    <Typography variant="body2" className="profile-card-text">
                      {userData?.email || "No email"}
                    </Typography>
                    <span
                      className="header-status-badge"
                      style={{ backgroundColor: getStatusStyle().color }}
                    >
                      {getStatusStyle().text}
                    </span>
                  </Box>
                  <Divider />
                  {(status === "trial" || status === "pending") && (
                    <MenuItem className="menu-item" onClick={skipTrial}>
                      <HowToRegIcon className="menu-item-icon" />
                      Skip Trial & Register
                    </MenuItem>
                  )}
                  {status !== "trial" && status !== "pending" && (
                    <MenuItem
                      className="menu-item"
                      onClick={() => navigate("/profile")}
                    >
                      <ManageAccountsIcon className="menu-item-icon" />
                      Profile
                    </MenuItem>
                  )}
                  {/* <MenuItem className="menu-item" onClick={() => navigate("/subscription-settings")}>
                    <MdSettings className="menu-item-icon" />
                    Subscription Settings
                  </MenuItem> */}
                  <MenuItem className="menu-item" onClick={handleLogout}>
                    <MdLogout className="menu-item-icon" />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Link to="/auth" className="nav-button">
                  Register for Free
                </Link>
                <Link
                  to="/auth?skipRegistration=true"
                  className="nav-button nav-button-login"
                >
                  Login
                </Link>
              </>
            )}
          </nav>

          {/* Hamburger icon (visible on small screens only via CSS) */}
          <div
            className={`hamburger ${isInQuizSession ? "disabled" : ""}`}
            onClick={isInQuizSession ? undefined : () => setDrawerOpen(true)}
            aria-label="Toggle navigation menu"
            role="button"
            tabIndex="0"
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isInQuizSession) setDrawerOpen(true);
            }}
            style={
              isInQuizSession
                ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" }
                : {}
            }
          >
            <FaBars />
          </div>
        </div>
      </header>

      {/* Separate Drawer component for small screens */}
      <DrawerMenu
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userData={userData}
        statusStyle={getStatusStyle()}
        role={role}
        navLinks={navLinks}
        showProfileDetails={profileExpanded}
        toggleProfileDetails={() => setProfileExpanded(!profileExpanded)}
        handleLogout={handleLogout}
        isInQuizSession={isInQuizSession}
      />
    </>
  );
};

export default Header;
